// Utilities to detect and convert pipe/JSON table-like content into HTML tables

const stripAsterisks = (s: string) => s.replace(/\*/g, '');
const unescapeMarkdown = (s: string) => s.replace(/\\([\\`*_\{}\[\]()#+\-.!|<>])/g, '$1');

const buildHtmlTable = (uniqueTableLines: string[]) => {
  let htmlTable = '<table class="table"><thead><tr>';
  const headers = uniqueTableLines[0]
    .split('|')
    .filter((cell) => cell.trim())
    .map((cell) => unescapeMarkdown(stripAsterisks(cell.trim())));
  const headerColCount = Math.max(headers.length, 1);
  headers.forEach((header) => {
    htmlTable += `<th>${header}</th>`;
  });
  htmlTable += '</tr></thead><tbody>';

  // Helper to emit one row with exactly headerColCount columns (pad missing with empty strings)
  const emitRow = (cols: string[]) => {
    const padded = Array.from({ length: headerColCount }, (_, idx) => unescapeMarkdown(stripAsterisks((cols[idx] || '').trim())));
    htmlTable += '<tr>';
    padded.forEach((cell) => {
      const cellContent = ['null', '-', ''].includes(cell) ? '' : cell;
      htmlTable += `<td>${cellContent}</td>`;
    });
    htmlTable += '</tr>';
  };

  // If header has two columns, allow smart split of cells like "REP01 Accrual report ..."
  const splitCodeAndDesc = (val: string): [string, string] | null => {
    const trimmed = unescapeMarkdown(stripAsterisks(val.trim()));
    const m = trimmed.match(/^([A-Za-z]{2,}\d{1,})\s+(.+)$/);
    if (m) return [m[1], m[2]];
    return null;
  };

  // Skip header and separator
  for (let i = 2; i < uniqueTableLines.length; i++) {
    const cells = uniqueTableLines[i]
      .split('|')
      .filter((cell) => cell.trim())
      .map((cell) => stripAsterisks(cell.trim()));

    if (cells.length === 0) continue;

    if (headerColCount === 2) {
      // Case A: row already has exactly two columns
      if (cells.length === 2) {
        emitRow(cells);
        continue;
      }
      // Case B: single line containing multiple entries where each cell is like "REP01 ..."
      const allCellsLookLikePairs = cells.every((c) => !!splitCodeAndDesc(c));
      if (allCellsLookLikePairs) {
        cells.forEach((c) => {
          const pair = splitCodeAndDesc(c)!;
          emitRow(pair);
        });
        continue;
      }
      // Case C: too many cells; chunk by header size as a fallback
      for (let j = 0; j < cells.length; j += headerColCount) emitRow(cells.slice(j, j + headerColCount));
      continue;
    }

    // General case: chunk data cells by header column count
    for (let j = 0; j < cells.length; j += headerColCount) emitRow(cells.slice(j, j + headerColCount));
  }

  htmlTable += '</tbody></table>';
  return htmlTable;
};

// Lifts the logic used by WizrMarkDown to detect/normalize table content
export const convertTableBlockToHtml = (content: string): string => {
  if (!content) return content;
  try {
    let working = content;

    // Handle JSON wrapper with .content field
    if (working.startsWith('{')) {
      const unescaped = working.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
      const parsed = JSON.parse(unescaped);
      if (parsed && typeof parsed === 'object' && typeof parsed.content === 'string') {
        working = stripAsterisks(parsed.content);
      }
    }

    let lines = working.split('\n').filter((line) => line.trim());
    let headerIdx = lines.findIndex((line) => line.includes('|'));
    const separatorRegex = /^\s*\|?[- ]+\|/;
    let sepIdx = lines.findIndex((line) => separatorRegex.exec(line));

    if (headerIdx !== -1 && (sepIdx === -1 || sepIdx !== headerIdx + 1)) {
      const headerCols = lines[headerIdx].split('|').filter((cell) => cell.trim()).length;
      const sepRow = '|' + Array(headerCols).fill('---').join('|') + '|';
      lines.splice(headerIdx + 1, 0, sepRow);
    }

    const tableLines: string[] = [];
    for (let i = headerIdx; i < lines.length; i++) {
      let line = lines[i];
      if (line.includes('|') || line.includes('\t')) {
        line = line.replace(/\t/g, '|').replace(/\s+\|\s+/g, '|').trim();
        if (!line.startsWith('|')) line = '|' + line;
        if (!line.endsWith('|')) line = line + '|';
        tableLines.push(line);
      } else if (line && line.split(/\s{2,}/).length > 2) {
        const cells = line.trim().split(/\s{2,}/).map((c) => c.trim());
        let row = '|' + cells.join('|') + '|';
        tableLines.push(row);
      }
    }

    if (tableLines.length >= 2) {
      const uniqueTableLines = tableLines.filter((row, idx, arr) => arr.indexOf(row) === idx);
      return buildHtmlTable(uniqueTableLines);
    }

    return content;
  } catch {
    return content;
  }
};

// Replace table-looking blocks in a larger content string with HTML table markup
export const replaceTableBlocksWithHtml = (content: string): string => {
  if (!content) return content;
  const blocks = content.split(/\n{2,}/);
  const transformed = blocks.map((block) => {
    const lines = block.trim().split('\n');
    const separatorRegex = /^(?:\|?-+\|?)+$/;
    const looksLikeTable =
      lines.length >= 2 && lines[0].includes('|') && separatorRegex.exec(lines[1].replace(/[ \t]/g, ''));
    if (looksLikeTable) {
      return convertTableBlockToHtml(block);
    }
    return block;
  });
  return transformed.join('\n\n');
};

// More robust: scan the whole text line-by-line and convert any inline table region to HTML
export const convertAnyPipeTablesToHtml = (content: string): string => {
  if (!content) return content;
  // First normalize inline separators like '||' or ' | |' into newlines
  let normalized = content.replace(/\|\|/g, '\n').replace(/\s\|\s\|/g, '\n|');
  // Remove lines that are just stray pipes (e.g., "|" or "|||") that can appear above/below tables
  normalized = normalized
    .split('\n')
    // Drop lines that contain only one or more pipes (e.g., "|" or "|||||")
    .filter((l) => !/^\s*\|+\s*$/.test(l))
    .join('\n');
  const lines = normalized.split('\n');
  const out: string[] = [];
  const isPipeLine = (l: string) => l.includes('|');
  const isSepLine = (l: string) => /^(\s*\|?\s*:?-{3,}:?(?:\s*\|\s*:?-{3,}:?)+\s*\|?\s*)$/.test(l.replace(/\s/g, ' '));
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    // a header-like line must include at least two pipes
    const pipeCount = (line.match(/\|/g) || []).length;
    // If the next line is not a separator, synthesize one so GFM tables still render
    if (pipeCount >= 2 && (i + 1 >= lines.length || !isSepLine(lines[i + 1]))) {
      const headerCells = line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .filter((c) => c.trim().length > 0);
      if (headerCells.length >= 2) {
        const sep = '|' + headerCells.map(() => '---').join('|') + '|';
        lines.splice(i + 1, 0, sep);
      }
    }
    if (pipeCount >= 2 && i + 1 < lines.length && isSepLine(lines[i + 1])) {
      const tableRegion: string[] = [];
      // ensure each row starts/ends with pipe
      const fixRow = (r: string) => {
        let row = r.trim();
        if (!row.startsWith('|')) row = '|' + row;
        if (!row.endsWith('|')) row = row + '|';
        // collapse extra spaces around pipes
        row = row.replace(/\s*\|\s*/g, '|');
        return row;
      };
      tableRegion.push(fixRow(line));
      tableRegion.push(fixRow(lines[i + 1]));
      i += 2;
      while (i < lines.length) {
        const rowLine = lines[i];
        if ((rowLine.match(/\|/g) || []).length >= 2) {
          tableRegion.push(fixRow(rowLine));
          i += 1;
        } else {
          break;
        }
      }
      out.push(buildHtmlTable(tableRegion));
      continue; // skip normal push
    }
    out.push(line);
    i += 1;
  }
  return out.join('\n');
};


